import { DURATIONS, DEFAULT_DURATION, GENDERS, DEFAULT_GENDER, GRID_COLUMNS } from '../constants/masterData';

/**
 * Grid row shape (also persisted as-is in salonServiceDrafts/{salonId}.rows):
 * { id, category, service, subService, gender, duration, price, masterServiceId }
 * A row with a non-empty subService is a child of the service named in its own
 * Service column; siblings repeat category+service+gender across rows.
 */

const makeId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function makeEmptyRow(defaults = {}) {
  return {
    id: makeId(),
    category: defaults.category || '',
    service: defaults.service || '',
    subService: defaults.subService || '',
    gender: defaults.gender || DEFAULT_GENDER,
    duration: defaults.duration || DEFAULT_DURATION,
    price: defaults.price ?? null,
    masterServiceId: defaults.masterServiceId || ''
  };
}

export function isRowEmpty(row) {
  return !row.category && !(row.service || '').trim() && !(row.subService || '').trim() && !row.price;
}

/** Snap an arbitrary minute value to the nearest allowed duration. */
export function coerceDuration(value) {
  const mins = Number(value);
  if (!mins || Number.isNaN(mins)) return DEFAULT_DURATION;
  return DURATIONS.reduce((best, d) => (Math.abs(d - mins) < Math.abs(best - mins) ? d : best), DURATIONS[0]);
}

export function coerceGender(value) {
  const v = (value || '').trim().toLowerCase();
  return GENDERS.find((g) => g.toLowerCase() === v) || DEFAULT_GENDER;
}

/**
 * Import a salon's existing servicesData.serviceCategories into grid rows.
 * Tolerates legacy field variants (categoryName/serviceList) and reads the
 * nested subServices shape if already present.
 */
export function rowsFromServiceCategories(serviceCategories) {
  const rows = [];
  if (!Array.isArray(serviceCategories)) return rows;

  serviceCategories.forEach((cat) => {
    const categoryName = cat.name || cat.categoryName || '';
    const services = cat.services || cat.serviceList || [];
    services.forEach((svc) => {
      const base = {
        category: categoryName,
        service: svc.name || '',
        // 'genderType' is the field name the mobile app (the source of truth for this data)
        // reads/writes; 'gender' is tolerated here only to read back anything saved by an
        // earlier build of this web editor before it matched mobile's field name.
        gender: coerceGender(svc.genderType ?? svc.gender),
        masterServiceId: svc.masterServiceId || ''
      };
      if (Array.isArray(svc.subServices) && svc.subServices.length) {
        svc.subServices.forEach((sub) => {
          rows.push(makeEmptyRow({
            ...base,
            subService: sub.name || '',
            duration: coerceDuration(sub.duration),
            price: sub.price != null ? Number(sub.price) : null
          }));
        });
      } else {
        rows.push(makeEmptyRow({
          ...base,
          duration: coerceDuration(svc.duration),
          price: svc.price != null ? Number(svc.price) : null
        }));
      }
    });
  });

  return rows;
}

/**
 * Transform grid rows into the published serviceCategories structure.
 * Grouping: category -> (service, gender). Groups containing subService rows
 * become a parent (no price/duration) with a subServices array.
 * @param {Array} rows
 * @param {Array<string>} [categoryOrder] - category names in display order (from masterCategories); unknown categories go last
 */
export function buildServiceCategoriesFromRows(rows, categoryOrder = []) {
  const nonEmpty = (rows || []).filter((r) => !isRowEmpty(r));

  const categoriesByName = new Map(); // preserves insertion order
  nonEmpty.forEach((row) => {
    const catName = (row.category || '').trim() || 'Others';
    if (!categoriesByName.has(catName)) categoriesByName.set(catName, new Map());
    const serviceGroups = categoriesByName.get(catName);

    const groupKey = `${(row.service || '').trim().toLowerCase()}|${row.gender}`;
    if (!serviceGroups.has(groupKey)) {
      serviceGroups.set(groupKey, { name: (row.service || '').trim(), gender: row.gender, rows: [] });
    }
    serviceGroups.get(groupKey).rows.push(row);
  });

  const orderIndex = new Map(categoryOrder.map((name, i) => [name.toLowerCase(), i]));
  const sortedCategoryNames = [...categoriesByName.keys()].sort((a, b) => {
    const ia = orderIndex.has(a.toLowerCase()) ? orderIndex.get(a.toLowerCase()) : Infinity;
    const ib = orderIndex.has(b.toLowerCase()) ? orderIndex.get(b.toLowerCase()) : Infinity;
    return ia - ib || a.localeCompare(b);
  });

  return sortedCategoryNames.map((catName) => ({
    name: catName,
    services: [...categoriesByName.get(catName).values()].map((group) => {
      const subRows = group.rows.filter((r) => (r.subService || '').trim());
      if (subRows.length) {
        return {
          name: group.name,
          genderType: group.gender, // field name matches mobile's schema — see rowsFromServiceCategories
          subServices: subRows.map((r) => ({
            name: r.subService.trim(),
            price: Number(r.price) || 0,
            duration: r.duration || DEFAULT_DURATION
          }))
        };
      }
      // plain service — single row carries price/duration
      const row = group.rows[0];
      return {
        name: group.name,
        genderType: group.gender, // field name matches mobile's schema — see rowsFromServiceCategories
        price: Number(row.price) || 0,
        duration: row.duration || DEFAULT_DURATION
      };
    })
  }));
}

/** Identity key for duplicate detection: category + service + subService + gender. */
export function rowDuplicateKey(row) {
  return [
    (row.category || '').trim().toLowerCase(),
    (row.service || '').trim().toLowerCase(),
    (row.subService || '').trim().toLowerCase(),
    row.gender
  ].join('|');
}

/**
 * Find duplicate rows (same category, service, sub service and gender).
 * Runs live while editing, and is also part of validateRows().
 * @returns {Array<{rowId, field, message}>}
 */
export function findDuplicateRowErrors(rows) {
  const errors = [];
  const seen = new Map();
  (rows || []).forEach((row, gridIndex) => {
    if (isRowEmpty(row) || !(row.service || '').trim()) return;
    const key = rowDuplicateKey(row);
    if (seen.has(key)) {
      errors.push({
        rowId: row.id,
        field: 'service',
        message: `Row ${gridIndex + 1} — Duplicate of row ${seen.get(key) + 1} (same category, service, sub service and gender)`
      });
    } else {
      seen.set(key, gridIndex);
    }
  });
  return errors;
}

/**
 * Stable-sort rows so services of the same category sit together.
 * Categories follow the master catalog order; unknown categories keep their
 * first-appearance order; rows without a category go last. Order within a
 * category is preserved, so inserts stay where the admin put them.
 */
export function groupRowsByCategory(rows, categoryOrder = []) {
  const orderIndex = new Map(categoryOrder.map((name, i) => [name.toLowerCase(), i]));
  const firstSeen = new Map();
  (rows || []).forEach((row, i) => {
    const key = (row.category || '').trim().toLowerCase();
    if (!firstSeen.has(key)) firstSeen.set(key, i);
  });
  const rank = (row) => {
    const key = (row.category || '').trim().toLowerCase();
    if (!key) return [2, 0];
    if (orderIndex.has(key)) return [0, orderIndex.get(key)];
    return [1, firstSeen.get(key)];
  };
  return (rows || [])
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const ra = rank(a.row);
      const rb = rank(b.row);
      return ra[0] - rb[0] || ra[1] - rb[1] || a.i - b.i;
    })
    .map((x) => x.row);
}

/**
 * Validate rows before publish.
 * @returns {Array<{rowId, field, message}>} empty when valid
 */
export function validateRows(rows) {
  const errors = [];
  // Keep original grid indices so error messages match the visible row numbers
  const nonEmpty = (rows || [])
    .map((row, gridIndex) => ({ row, gridIndex }))
    .filter(({ row }) => !isRowEmpty(row));

  const groupHasSubs = new Map();

  nonEmpty.forEach(({ row }) => {
    const groupKey = `${(row.category || '').trim().toLowerCase()}|${(row.service || '').trim().toLowerCase()}|${row.gender}`;
    if ((row.subService || '').trim()) groupHasSubs.set(groupKey, true);
  });

  nonEmpty.forEach(({ row, gridIndex }) => {
    const rowLabel = `Row ${gridIndex + 1}`;
    if (!(row.category || '').trim()) {
      errors.push({ rowId: row.id, field: 'category', message: `${rowLabel} — Category is required` });
    }
    if (!(row.service || '').trim()) {
      errors.push({ rowId: row.id, field: 'service', message: `${rowLabel} — Service name is required` });
    }
    if (!row.gender) {
      errors.push({ rowId: row.id, field: 'gender', message: `${rowLabel} — Gender is required` });
    }
    if (!row.duration) {
      errors.push({ rowId: row.id, field: 'duration', message: `${rowLabel} — Duration is required` });
    }
    if (!(Number(row.price) > 0)) {
      errors.push({ rowId: row.id, field: 'price', message: `${rowLabel} — Price must be greater than 0` });
    }

    const groupKey = `${(row.category || '').trim().toLowerCase()}|${(row.service || '').trim().toLowerCase()}|${row.gender}`;
    if (groupHasSubs.get(groupKey) && !(row.subService || '').trim()) {
      errors.push({
        rowId: row.id,
        field: 'subService',
        message: `${rowLabel} — "${row.service}" has sub services; give this row a sub service name or remove it`
      });
    }

  });

  return errors.concat(findDuplicateRowErrors(rows));
}

/**
 * Flag rows whose gender doesn't match a gender-specific salon (Men-only or Women-only).
 * Unisex salons serve everyone, so nothing is flagged for them. A Men-only salon should
 * only carry "Men" rows — a "Women" or "Unisex" row is very likely a mistake (e.g. picked
 * from master-catalog autocomplete without checking), so both are flagged, not just the
 * opposite gender.
 * @param {Array} rows
 * @param {string} servicesFor - salon's profile.servicesFor ('Men' | 'Women' | 'Unisex')
 * @returns {Array<{rowId, field, message}>} empty when valid or salon is Unisex
 */
export function validateGenderMatch(rows, servicesFor) {
  if (servicesFor !== 'Men' && servicesFor !== 'Women') return [];
  const errors = [];
  (rows || [])
    .map((row, gridIndex) => ({ row, gridIndex }))
    .filter(({ row }) => !isRowEmpty(row))
    .forEach(({ row, gridIndex }) => {
      if (row.gender && row.gender !== servicesFor) {
        const label = row.subService ? `${row.service} - ${row.subService}` : row.service || 'This row';
        errors.push({
          rowId: row.id,
          field: 'gender',
          message: `Row ${gridIndex + 1} — "${label}" is tagged ${row.gender}, but this is a ${servicesFor}-only salon. Change the gender to ${servicesFor} or remove this row.`
        });
      }
    });
  return errors;
}

// ---------- Combos ----------
// Draft shape: { id, title, totalPrice, services: [{ name, price }] }
// Published shape matches the mobile app: { title, totalPrice, services: [{ name, price }] }

export function makeEmptyCombo() {
  return { id: makeId(), title: '', totalPrice: null, services: [] };
}

/**
 * Import a salon's existing servicesData.combos into draft combos.
 * Tolerates legacy variants (name/price instead of title/totalPrice,
 * plain-string service entries).
 */
export function combosFromServicesData(combos) {
  if (!Array.isArray(combos)) return [];
  return combos.map((c) => ({
    id: makeId(),
    title: c.title || c.name || '',
    totalPrice: c.totalPrice != null || c.price != null ? Number(c.totalPrice ?? c.price) || null : null,
    services: (c.services || []).map((s) =>
      typeof s === 'string'
        ? { name: s, price: null }
        : { name: s.name || '', price: s.price != null ? Number(s.price) || null : null }
    )
  }));
}

/** Transform draft combos into the published combos structure. */
export function buildCombosForPublish(combos) {
  return (combos || [])
    .filter((c) => (c.title || '').trim())
    .map((c) => ({
      title: c.title.trim(),
      totalPrice: Number(c.totalPrice) || 0,
      services: (c.services || [])
        .filter((s) => (s.name || '').trim())
        .map((s) => ({ name: s.name.trim(), price: Number(s.price) || 0 }))
    }));
}

/**
 * Validate combos before publish.
 * @returns {Array<{comboId, message}>} empty when valid
 */
export function validateCombos(combos) {
  const errors = [];
  const seenTitles = new Map();
  (combos || []).forEach((combo, index) => {
    const title = (combo.title || '').trim();
    const services = (combo.services || []).filter((s) => (s.name || '').trim());
    // Fully empty combos are ignored (like empty rows)
    if (!title && !services.length && !combo.totalPrice) return;

    const label = `Combo ${index + 1}${title ? ` ("${title}")` : ''}`;
    if (!title) {
      errors.push({ comboId: combo.id, message: `${label} — Combo name is required` });
    }
    if (!(Number(combo.totalPrice) > 0)) {
      errors.push({ comboId: combo.id, message: `${label} — Combo price must be greater than 0` });
    }
    if (services.length < 2) {
      errors.push({ comboId: combo.id, message: `${label} — Include at least 2 services` });
    }
    const key = title.toLowerCase();
    if (title) {
      if (seenTitles.has(key)) {
        errors.push({ comboId: combo.id, message: `${label} — Duplicate combo name` });
      } else {
        seenTitles.set(key, index);
      }
    }
  });
  return errors;
}

/** Serialize rows as tab-separated values (Excel/Sheets compatible). */
export function rowsToTsv(rows) {
  return (rows || [])
    .map((row) => GRID_COLUMNS.map((col) => (row[col] ?? '')).join('\t'))
    .join('\n');
}

/**
 * Parse TSV text (from Excel/Sheets or rowsToTsv) into grid rows.
 * Column order matches GRID_COLUMNS; durations snap to the allowed list,
 * unknown genders fall back to the default.
 */
export function tsvToRows(text) {
  return (text || '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim())
    .map((line) => {
      const cells = line.split('\t');
      const priceRaw = (cells[5] || '').replace(/[^\d.]/g, '');
      return makeEmptyRow({
        category: (cells[0] || '').trim(),
        service: (cells[1] || '').trim(),
        subService: (cells[2] || '').trim(),
        gender: coerceGender(cells[3]),
        duration: coerceDuration(cells[4]),
        price: priceRaw ? Number(priceRaw) : null
      });
    });
}
